import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { object, string } from 'yup';

import { useCardReader } from 'Src/feature/card-reader/useCardReader';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { useEmployee } from 'Src/feature/employee/useEmployee';
import { getTypedFormComponents } from 'Src/feature/form/getTypedFormComponents';
import { useForm } from 'Src/feature/form/useForm';
import { CurrentOperator } from 'Src/feature/operator/operatorService';
import stashedCredService from 'Src/feature/operator/stashedCredService';
import { useCurrentOperator } from 'Src/feature/operator/useCurrentOperator';
import { useOperator } from 'Src/feature/operator/useOperator';
import { useIsClockedIn } from 'Src/feature/terminal/useIsClockedIn';
import { useTerminal } from 'Src/feature/terminal/useTerminal';
import { useToast } from 'Src/feature/toasts/useToast';
import { useError } from 'Src/network/useError';
import useRoute from 'Src/routes/useRoute';
import { IS_LOCAL_DEV, NODE_ENV } from 'Src/stores/appEnv';
import { navigateLocalStorage } from 'Src/stores/navigateLocalStorage';

interface FormModel {
  pin: string | undefined;
  swipe: string | undefined;
}

const { Form, Field } = getTypedFormComponents<FormModel>();

const sectionId = 'usePOSLogin';

const usePOSLogin = () => {
  const log = useLogger().getSubLogger({ name: sectionId });
  const { t } = useTranslation();
  const { navigate, crumbs, location, route } = useRoute();
  const { parseError } = useError();
  const toast = useToast();
  const cardReader = useCardReader();

  const terminalLocation = useTerminal.query.get();
  const operatorClockIn = useOperator.mutate.clockIn();
  const operatorLogin = useOperator.mutate.login();
  const operatorEndBreak = useOperator.mutate.endBreak();
  const operatorLogout = useOperator.mutate.logout();
  const employeeInvalidate = useEmployee.query.invalidate();

  const { currentOperator, setCurrentOperator } = useCurrentOperator();
  const { isClockedIn, setIsClockedIn } = useIsClockedIn();

  const [showErrorModal, showErrorModalSet] = useState(false);
  const [serverErrorMsg, serverErrorMsgSet] = useState(undefined as undefined | string);
  const [showEndBreakModal, showEndBreakModalSet] = useState(false);
  const [availableJobPositions, availableJobPositionsSet] = useState([] as CurrentOperator['availableJobPositions']);
  const jobPositionOptions = availableJobPositions?.map(({ id, name }) => ({ value: id as number, label: name }));

  const form = useForm<FormModel>({
    defaultValues: { pin: undefined, swipe: undefined },
    schema: object().shape({
      pin: string().label(t('labels.pin')).optional().min(3),
      swipe: string().
        optional().
        min(3).
        test(
          'invalid-swipe',
          t('payment.info.pleaseSwipeCard'),
          (value) => !value?.toLowerCase().includes('invalid')
        ),
    }),
  });

  const logIn = async (jobPositionId?: number) => {
    await form.action.submitIfValid(async ({ values }) => {
      try {
        const { pin, swipe } = values;
        const response = jobPositionId
          ? await operatorClockIn.mutateAsync({ authNRequest: { pin, cardCode: swipe, jobPositionId } })
          : await operatorLogin.mutateAsync({ authNRequest: { pin, cardCode: swipe } });

        if (!response) {
          reInitCardReader();
          throw new Error('!response');
        }

        // the current operator requires employee data to be available from the employee.getAll
        // - if a no employees have been loaded we invalidate the employee.getAll
        // - if a matching employee is not found we invalidate the employee.getAll
        employeeInvalidate.getAllByOne(response.employee.id);
        // the current operator requires shift data to be available and up-to-date for printing shift reports
        employeeInvalidate.getShifts();
        // TODO: What do we want to show them in the UI if they
        // log in while on break and don't want to end it?
        // Is there anything at all they should see ?

        // If the current shift is currently on break, prompt to see if operator wants to end the break.
        if (response.shift?.isOnBreak) {
          showEndBreakModalSet(true);
        } else if (response.availableJobPositions && response.availableJobPositions.length) {
          availableJobPositionsSet(response.availableJobPositions);
        } else {
          // operator successfully clocked in
          // we will have a shift and currentJobPosition
          setCurrentOperator(response);
          setIsClockedIn(true);

          // now we have to figure out where to route to (check is the default)
          // but, if we were redirected here from somewhere else, the redirection should
          // be wired up to put the location they routed from into location.state.referrer
          const routeState = location.state as { referrer?: { pathName?: string } } | undefined;
          let nextRoute: string;
          if (routeState && routeState.referrer?.pathName) {
            // we were redirected properly and know the path we were referred from, so we can just use that entire location
            nextRoute = routeState.referrer?.pathName;
            log.info('navigate from location state: ', { nextRoute });
          } else {
            // handle terminal default settings
            if (terminalLocation.data?.terminal.terminalSettings.defaultView) {
              nextRoute = crumbs.pos.checks.main.interpolate({
                tabId: route.params.value.defaultView,
              });
            } else {
              nextRoute = crumbs.pos.checks.main.interpolate({
                tabId: route.params.value.checksList,
              });
              log.info('navigate to default starting route: ', { nextRoute });
            }
            // when isDev and not a test run
            // - DX improvement to jump to the last route developer was on
            if (IS_LOCAL_DEV && NODE_ENV !== 'test') {
              const historyItems = (navigateLocalStorage.get()?.history.items || []).reverse();
              if (historyItems.length) {
                const shouldUse = route.getRedirectState(historyItems[0].pathname) !== undefined;
                if (shouldUse) {
                  nextRoute = historyItems[0].pathname;
                  log.info('navigate to last route during DEV: ', { nextRoute });
                }
              }
            }
          }
          navigate(nextRoute);
        }
      } catch (error) {
        log.error('logIn()', error);
        showErrorModalSet(true);
        const parsedError = parseError(error);
        // set invalid PIN message when status === 400, otherwise use whatever the server sent back
        if (parsedError.status === 400) serverErrorMsgSet(t('posLogin.invalidLoginDialog.defaultMsg'));
        else serverErrorMsgSet(parsedError.message);
        // reset form
        form.action.reset();
        availableJobPositionsSet([]);
        reInitCardReader();
      }
    });
  };

  const reInitCardReader = () => {
    cardReader.init({
      mode: 'msr',
      amount: 0,
      onSuccess: (read) => {
        if (!read || !read.pax) {
          // TODO: when would this happen
          log.error('cardReader.onSuccess()', 'no pax data received');
          return;
        }

        if (cardReader.is.employeeSwipeInvalid(read.pax)) {
          read.reInit();
        }

        form.action.setValue('pin', undefined, { shouldValidate: true, shouldTouch: true, shouldDirty: true });
        form.action.setValue('swipe', read.pax.employeeSwipeCode, { shouldValidate: true, shouldTouch: true, shouldDirty: true });
        void logIn();
      },
      onError: () => true,
    });
  };

  useEffect(() => {
    // "cardReader.connect" should only happen once
    //  - is stable (internal parts use refs)
    //  - very important that this only runs once (when first mounting)
    void cardReader.connect();
    // "stashed creds" should only happen once
    //  - if we're already clocked in, the redirect referrer goes back to log in so don't call "login" again
    const stashedCreds = stashedCredService.retrieveCreds();
    if ((stashedCreds.pin || stashedCreds.swipe) && !(isClockedIn && currentOperator)) {
      log.info('logging in w/ stashed credentials');
      form.action.reset(stashedCreds);
      void logIn();
    }
    return () => {
      void cardReader.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    form,
    showErrorModal,
    serverErrorMsg,
    showEndBreakModal,
    availableJobPositions,
    jobPositionOptions,
    terminalLocation,
    logIn,
    onErrorModalOk: () => {
      serverErrorMsgSet(undefined);
      showErrorModalSet(false);
    },
    onDoNotEndBreakModal: async () => {
      form.action.setValue('pin', '');
      showEndBreakModalSet(false);
      try {
        await operatorLogout.mutateAsync();
      } catch (err) {
        log.error('onDoNotEndBreakModal.logout', err);
        toast.show.serviceError(err, { id: `${sectionId}.operatorLogout`, title: t('posLogin.errors.logout') });
      }
    },
    onEndBreakModal: async () => {
      showEndBreakModalSet(false);
      try {
        await operatorEndBreak.mutateAsync();
        await logIn();
      } catch (err) {
        log.error('onEndBreakModal.endBreak', err);
        toast.show.serviceError(err, { id: `${sectionId}.operatorEndBreak`, title: t('posLogin.errors.endBreak') });
      }
    },
    reInitCardReader,
    isLoading: operatorLogin.isPending || operatorClockIn.isPending,
  };
};

export { Field, Form, usePOSLogin };
